import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  InputBase,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Typography,
  Divider,
  ListItemIcon,
  Tooltip,
  alpha,
  Chip,
} from '@mui/material';
import {
  Search,
  Bell,
  Settings,
  User,
  LogOut,
  HelpCircle,
  Moon,
  Sun,
  Command,
} from 'lucide-react';
import { useNotification } from '../../contexts/NotificationContext';

function TopBar({ onJobSelect, onOpenNotifications, onOpenSearch }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [darkMode, setDarkMode] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const notification = useNotification();

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (event) => {
    if (onOpenNotifications) {
      onOpenNotifications();
    } else {
      setNotificationAnchor(event.currentTarget);
    }
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter' && searchValue.trim()) {
      notification.info(`Searching for "${searchValue}"...`);
    }
  };

  const handleSettingsClick = () => {
    handleClose();
    notification.success('Opening settings...');
  };

  const handleSignOut = () => {
    handleClose();
    notification.warning('Signing out...');
  };

  return (
    <AppBar 
      position="sticky" 
      color="inherit" 
      elevation={0}
      sx={{ 
        borderBottom: '1px solid',
        borderColor: 'rgba(226, 232, 240, 0.8)',
        bgcolor: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(20px)',
      }}
    >
      <Toolbar sx={{ gap: 2, minHeight: { xs: 64, sm: 72 } }}>
        {/* Search Bar */}
        <Box
          onClick={() => onOpenSearch?.()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: searchFocused ? 'white' : alpha('#F1F5F9', 0.8),
            borderRadius: 3,
            px: 2,
            py: 1,
            flex: 1,
            maxWidth: 500,
            border: '1px solid',
            borderColor: searchFocused ? 'primary.main' : 'transparent',
            boxShadow: searchFocused ? '0 0 0 3px rgba(99, 102, 241, 0.1)' : 'none',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'white',
              borderColor: alpha('#6366F1', 0.3),
            },
          }}
        >
          <Search size={18} color={searchFocused ? '#6366F1' : '#64748B'} />
          <InputBase
            placeholder="Search jobs, clients, estimates..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={handleSearch}
            onFocus={() => {
              setSearchFocused(true);
              if (onOpenSearch) {
                onOpenSearch();
              }
            }}
            onBlur={() => setSearchFocused(false)}
            sx={{ ml: 1.5, flex: 1, fontSize: 14, cursor: 'pointer' }}
            readOnly={!!onOpenSearch}
          />
          <Chip
            size="small"
            icon={<Command size={12} />}
            label="/"
            sx={{ 
              height: 24,
              bgcolor: 'rgba(0,0,0,0.04)',
              '& .MuiChip-icon': { fontSize: 12 },
              '& .MuiChip-label': { px: 0.5, fontWeight: 600, fontSize: '0.7rem' },
            }}
          />
        </Box>

        <Box sx={{ flex: 1 }} />

        {/* Actions */}
        <Tooltip title={darkMode ? 'Light Mode' : 'Dark Mode'} arrow>
          <IconButton 
            onClick={() => {
              setDarkMode(!darkMode);
              notification.info(darkMode ? 'Light mode enabled' : 'Dark mode enabled');
            }} 
            sx={{ 
              bgcolor: 'rgba(0,0,0,0.03)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
            }}
          >
            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Help & Support" arrow>
          <IconButton 
            onClick={() => notification.info('Opening help center...')}
            sx={{ 
              bgcolor: 'rgba(0,0,0,0.03)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
            }}
          >
            <HelpCircle size={20} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Notifications" arrow>
          <IconButton 
            onClick={handleNotificationClick}
            sx={{ 
              bgcolor: 'rgba(0,0,0,0.03)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
            }}
          >
            <Badge 
              badgeContent={3} 
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.65rem',
                  height: 18,
                  minWidth: 18,
                },
              }}
            >
              <Bell size={20} />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* Profile */}
        <IconButton 
          onClick={handleProfileClick} 
          sx={{ 
            p: 0.5,
            border: '2px solid transparent',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: 'primary.main',
            },
          }}
        >
          <Avatar 
            sx={{ 
              width: 36, 
              height: 36, 
              background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
              fontWeight: 600,
            }}
          >
            U
          </Avatar>
        </IconButton>

        {/* Profile Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: { 
              width: 240, 
              mt: 1.5,
              borderRadius: 3,
              boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.2)',
            }
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={700}>John Doe</Typography>
            <Typography variant="caption" color="text.secondary">
              john@fusionsigns.com
            </Typography>
          </Box>
          <Divider />
          <MenuItem 
            onClick={handleClose}
            sx={{ py: 1.5, borderRadius: 2, mx: 1, mt: 1 }}
          >
            <ListItemIcon><User size={18} /></ListItemIcon>
            Profile
          </MenuItem>
          <MenuItem 
            onClick={handleSettingsClick}
            sx={{ py: 1.5, borderRadius: 2, mx: 1 }}
          >
            <ListItemIcon><Settings size={18} /></ListItemIcon>
            Settings
          </MenuItem>
          <Divider sx={{ my: 1 }} />
          <MenuItem 
            onClick={handleSignOut}
            sx={{ 
              py: 1.5, 
              borderRadius: 2, 
              mx: 1, 
              mb: 1,
              color: 'error.main',
              '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.08)' },
            }}
          >
            <ListItemIcon><LogOut size={18} color="#EF4444" /></ListItemIcon>
            Sign Out
          </MenuItem>
        </Menu>

        {/* Notifications Menu */}
        <Menu
          anchorEl={notificationAnchor}
          open={Boolean(notificationAnchor)}
          onClose={handleNotificationClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: { 
              width: 360, 
              maxHeight: 440, 
              mt: 1.5,
              borderRadius: 3,
              boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.2)',
            }
          }}
        >
          <Box sx={{ px: 2, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Notifications
            </Typography>
            <Chip 
              label="3 new" 
              size="small" 
              color="primary"
              sx={{ fontWeight: 600, fontSize: '0.7rem' }}
            />
          </Box>
          <Divider />
          <MenuItem 
            onClick={() => {
              handleNotificationClose();
              notification.success('Viewing permit details...');
            }}
            sx={{ py: 2 }}
          >
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: 'rgba(16, 185, 129, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                ✅
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  New permit approved for Job #1234
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  2 minutes ago
                </Typography>
              </Box>
            </Box>
          </MenuItem>
          <MenuItem 
            onClick={() => {
              handleNotificationClose();
              notification.info('Opening design review...');
            }}
            sx={{ py: 2 }}
          >
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: 'rgba(245, 158, 11, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                🎨
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Design review needed for ABC Corp
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  15 minutes ago
                </Typography>
              </Box>
            </Box>
          </MenuItem>
          <MenuItem 
            onClick={() => {
              handleNotificationClose();
              notification.success('Viewing production details...');
            }}
            sx={{ py: 2 }}
          >
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: 'rgba(99, 102, 241, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                🏭
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Production complete for Job #1230
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  1 hour ago
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}

export default TopBar;
